import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentResult({ success = false }) {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState(success ? "checking" : "cancelled");
  const [orderId, setOrderId] = useState(null);
  const cleared = useRef(false);
  const { clear } = useCart();

  useEffect(() => {
    if (!success || !sessionId) {
      if (success) setState("paid"); // credit-paid orders land here — payment already confirmed server-side
      return;
    }
    let stop = false;
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrderId(data.order_id);
          setState("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
            toast.success("Payment confirmed — thank you!");
          }
          return;
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
      tries += 1;
      if (!stop && tries < 24) setTimeout(tick, 2500);
      else if (!stop) setState("pending");
    };
    tick();
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, success]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#FAF8F4] px-4 text-center">
      {state === "paid" && (
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center">
          <CheckCircle2 className="h-16 w-16 text-[#C79500]" />
          <h1 className="mt-6 font-display text-5xl font-black uppercase tracking-tight text-stone-950">Order confirmed</h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-stone-500">
            Payment received — we're on it. We'll check your piece and get it on its way.
            Keep an eye on your inbox.
          </p>
          {orderId && <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-stone-400">Order {orderId.slice(0, 8).toUpperCase()}</p>}
          <Link to="/shop" className="mt-8 bg-[#E52E39] px-10 py-4 font-display text-lg font-black uppercase tracking-wide text-white hover:bg-[#c9202a]">
            Keep shopping
          </Link>
        </motion.div>
      )}
      {state === "checking" && (
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="h-10 w-10 animate-spin text-[#C79500]" />
          <p className="font-display text-2xl font-bold uppercase tracking-wide text-stone-950">Confirming your payment…</p>
          <p className="text-sm text-stone-500">This only takes a moment.</p>
        </div>
      )}
      {state === "pending" && (
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="h-10 w-10 text-[#C79500]" />
          <h1 className="font-display text-4xl font-black uppercase text-stone-950">Payment processing</h1>
          <p className="max-w-md text-sm text-stone-500">
            Your payment is still confirming with the bank. We'll honour your order — check back shortly.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 border border-stone-400 px-8 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-stone-950 hover:border-[#E52E39]"
          >
            Refresh
          </button>
        </div>
      )}
      {state === "cancelled" && (
        <div className="flex flex-col items-center">
          <XCircle className="h-16 w-16 text-[#E52E39]" />
          <h1 className="mt-6 font-display text-5xl font-black uppercase tracking-tight text-stone-950">Payment cancelled</h1>
          <p className="mt-4 max-w-md text-sm text-stone-500">No money has been taken. Your bag is still waiting for you.</p>
          <Link to="/checkout" className="mt-8 bg-[#E52E39] px-10 py-4 font-display text-lg font-black uppercase tracking-wide text-white hover:bg-[#c9202a]">
            Back to checkout
          </Link>
        </div>
      )}
      {state === "error" && (
        <div className="flex flex-col items-center">
          <h1 className="font-display text-4xl font-black uppercase text-stone-950">Something went wrong</h1>
          <Link to="/shop" className="mt-6 bg-[#E52E39] px-8 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white">
            Back to the shop
          </Link>
        </div>
      )}
    </div>
  );
}
